import React from 'react'
import { useWindowDimensions, View, ViewStyle } from 'react-native'

import ZKXLogo from '@common/components/ZKXLogo'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

export const UNLOCK_PANEL_WIDTH = 480
// Below this width the unlock screen drops the centered floating card and renders
// full-screen on a flat page, matching the onboarding screens (OnboardingCard).
// Mirrors ONBOARDING_FULLSCREEN_MAX_WIDTH so unlock + onboarding stay consistent.
export const UNLOCK_FULLSCREEN_MAX_WIDTH = 600

type Props = {
  children: React.ReactNode
  // Optional content rendered below the title, before the form (e.g. biometrics prompt)
  title?: string
  cardStyle?: ViewStyle
}

// Centered "Welcome back" card shared by every render target (extension popup/tab,
// mobile-web SPA and native mobile). Caps its width at 480px and centers itself in
// whatever space the parent screen container gives it.
const UnlockPanel: React.FC<Props> = ({ children, title, cardStyle }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { width: windowWidth } = useWindowDimensions()
  const isFullScreen = windowWidth < UNLOCK_FULLSCREEN_MAX_WIDTH

  const inner = (
    <>
      <View style={spacings.mbSm}>
        <ZKXLogo width={88} />
      </View>
      <Text
        fontSize={28}
        weight="semiBold"
        color={theme.primaryText}
        style={[text.center, spacings.mbTy]}
      >
        {title || t('Welcome back')}
      </Text>
      <Text fontSize={16} weight="medium" color="#818997" style={[text.center, spacings.mbXl]}>
        {t('Easy and secure self-custody for the\nMulti-chain ecosystem')}
      </Text>
      <View style={{ width: '100%' }}>{children}</View>
    </>
  )

  // Narrow widths (mobile-web on a phone, native mobile, the popup): full-screen
  // flat layout, no card, content vertically centered. Matches OnboardingCard.
  if (isFullScreen) {
    return (
      <View
        style={[
          flexbox.flex1,
          flexbox.alignCenter,
          flexbox.justifyCenter,
          spacings.ph,
          { width: '100%', backgroundColor: theme.onboardingPageBackground }
        ]}
      >
        <View style={[{ width: '100%' }, flexbox.alignCenter]}>{inner}</View>
      </View>
    )
  }

  return (
    <View style={[flexbox.flex1, flexbox.alignCenter, flexbox.justifyCenter, spacings.ph]}>
      <View
        style={[
          {
            width: UNLOCK_PANEL_WIDTH,
            maxWidth: '90%',
            backgroundColor: theme.onboardingCardBackground,
            borderRadius: 24,
            paddingHorizontal: 32,
            paddingVertical: 40,
            shadowColor: theme.onboardingCardShadow,
            shadowOffset: { width: 0, height: 12 },
            shadowOpacity: 1,
            shadowRadius: 32,
            elevation: 12
          },
          flexbox.alignCenter,
          cardStyle
        ]}
      >
        {inner}
      </View>
    </View>
  )
}

export default React.memo(UnlockPanel)
